import React, { useState, useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import MainLayout from "../Themes/MainLayout";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import Download from "@mui/icons-material/Download";
import PlayArrow from "@mui/icons-material/PlayArrow";
import * as XLSX from "xlsx";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BRAND = "#2DB0E4";
const BRAND_DARK = "#1D87B0";
const PAGE_SIZE = 15;

// ─── Report definitions ────────────────────────────────────────────────────
const REPORT_TYPES = [
  {
    key: "attendance",
    label: "Attendance Report",
    description:
      "No-entry days (excluding weekends, holidays & approved leave) and late-logged entries per employee.",
    endpoint: "/api/reports/attendance",
    columns: [
      { field: "emp_id", header: "Emp ID" },
      { field: "first_name", header: "First Name" },
      { field: "last_name", header: "Last Name" },
      { field: "noentry_count", header: "No-Entry Days" },
      { field: "noentry_dates", header: "No-Entry Dates" },
      { field: "on_leave_days", header: "On-Leave Days" },
      { field: "late_entry_days", header: "Late-Entry Days" },
      { field: "late_entry_dates", header: "Late-Entry Details (Attendance # Created)" },
    ],
  },
  {
    key: "permission",
    label: "Permission Report",
    description:
      "Approved hourly permission (Specific Hours) leaves per employee, with total hours and dates.",
    endpoint: "/api/reports/permission",
    columns: [
      { field: "emp_id", header: "Emp ID" },
      { field: "first_name", header: "First Name" },
      { field: "last_name", header: "Last Name" },
      { field: "permission_count", header: "Permission Count" },
      { field: "total_hours", header: "Total Hours" },
      { field: "permission_dates", header: "Permission Dates (date (hours, time))" },
    ],
  },
  {
    key: "leave",
    label: "Leave Report",
    description:
      "Detailed leave requests overlapping the date range (excludes hourly permission). Filter by status.",
    endpoint: "/api/reports/leave",
    hasStatus: true,
    columns: [
      { field: "emp_id", header: "Emp ID" },
      { field: "first_name", header: "First Name" },
      { field: "last_name", header: "Last Name" },
      { field: "leave_type", header: "Leave Type" },
      { field: "duration_type", header: "Duration" },
      { field: "start_date", header: "Start Date" },
      { field: "end_date", header: "End Date" },
      { field: "total_days", header: "Days" },
      { field: "total_hours", header: "Hours" },
      { field: "status", header: "Status" },
      { field: "approver_name", header: "Approver" },
      { field: "leave_description", header: "Description" },
    ],
  },
];

const LEAVE_STATUS_OPTIONS = ["Approved", "Pending", "Rejected", "Cancelled", "All"];

// First and last day of the current month as YYYY-MM-DD
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// Shared height so toolbar controls align (matches other pages' 36px controls).
// margin:0 neutralizes FormThemes' default `margin: "dense"` so the row stays aligned.
const controlSx = {
  m: 0,
  "& .MuiInputBase-root": { height: 36 },
  "& .MuiInputLabel-root": { fontSize: "14px" },
};

export default function Reports() {
  const { boId } = useCurrentUser();

  const [reportKey, setReportKey] = useState("attendance");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState("Approved");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [generatedFor, setGeneratedFor] = useState(null); // { reportKey, from, to, status }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  const reportDef = useMemo(
    () => REPORT_TYPES.find((r) => r.key === reportKey),
    [reportKey]
  );

  const validRange = from && to && from <= to;

  // ── Generate (fetch) ───────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!boId) {
      setError("User organization not loaded yet. Please try again in a moment.");
      return;
    }
    if (!validRange) {
      setError("Please provide a valid date range (From must be on or before To).");
      return;
    }
    setError("");
    setLoading(true);
    setRows([]);
    setPage(1);
    try {
      let url = `${BASE_URL}${reportDef.endpoint}?bo_id=${encodeURIComponent(
        boId
      )}&from=${from}&to=${to}`;
      if (reportDef.hasStatus) {
        url += `&status=${encodeURIComponent(status)}`;
      }
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to generate report (${res.status})`);
      }
      const payload = await res.json();
      const data = Array.isArray(payload) ? payload : payload.data || [];
      setRows(data);
      setGeneratedFor({
        reportKey,
        from,
        to,
        status: reportDef.hasStatus ? status : null,
      });
      if (data.length === 0) {
        showSnack("Report generated — no matching records for this range.", "info");
      } else {
        showSnack(`Report generated: ${data.length} record(s).`);
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Download as Excel ────────────────────────────────────────────────────────
  const handleDownload = () => {
    if (!rows.length) {
      showSnack("Nothing to download. Generate the report first.", "warning");
      return;
    }
    const def = REPORT_TYPES.find((r) => r.key === generatedFor.reportKey) || reportDef;

    const aoa = [
      def.columns.map((c) => c.header),
      ...rows.map((row) =>
        def.columns.map((c) => {
          const v = row[c.field];
          return v === null || v === undefined ? "" : v;
        })
      ),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = def.columns.map((c) => ({
      wch: Math.max(12, Math.min(50, c.header.length + 4)),
    }));

    const wb = XLSX.utils.book_new();
    const sheetName = def.label.replace(/[^\w ]/g, "").slice(0, 31) || "Report";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const statusPart = generatedFor.status ? `_${generatedFor.status}` : "";
    const fileName = `${def.key}_report${statusPart}_${generatedFor.from}_to_${generatedFor.to}.xlsx`;
    XLSX.writeFile(wb, fileName);
    showSnack("Download started.");
  };

  // ── Filter + paginate preview rows ───────────────────────────────────────────
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.emp_id, r.first_name, r.last_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;
  const paginatedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeColumns =
    (generatedFor &&
      REPORT_TYPES.find((r) => r.key === generatedFor.reportKey)?.columns) ||
    reportDef.columns;

  return (
    <ThemeProvider theme={Table_theme}>
      <CssBaseline />
      <MainLayout>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Toolbar: report type + inputs + actions ── */}
        <ThemeProvider theme={FormThemes}>
        <Box display="flex" alignItems="center" mb={2} gap={1.5} flexWrap="wrap">
          <TextField
            select
            fullWidth={false}
            size="small"
            label="Report Type"
            value={reportKey}
            onChange={(e) => {
              setReportKey(e.target.value);
              setRows([]);
              setGeneratedFor(null);
              setError("");
              setPage(1);
            }}
            sx={{ width: 220, ...controlSx }}
          >
            {REPORT_TYPES.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth={false}
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to || undefined }}
            sx={{ width: 170, ...controlSx }}
          />
          <TextField
            fullWidth={false}
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from || undefined }}
            sx={{ width: 170, ...controlSx }}
          />

          {reportDef.hasStatus && (
            <TextField
              select
              fullWidth={false}
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ width: 150, ...controlSx }}
            >
              {LEAVE_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleGenerate}
            disabled={loading || !validRange}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />
            }
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              backgroundColor: BRAND,
              "&:hover": { backgroundColor: BRAND_DARK },
            }}
          >
            {loading ? "Generating…" : "Generate"}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleDownload}
            disabled={!rows.length}
            startIcon={<Download />}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2.5,
              height: 36,
              color: BRAND,
              borderColor: BRAND,
              "&:hover": { borderColor: BRAND_DARK, backgroundColor: "rgba(45,176,228,0.08)" },
            }}
          >
            Download
          </Button>
        </Box>

        {/* Report description + preview meta */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="#777">
            {reportDef.description}
          </Typography>

          {generatedFor && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`${filteredRows.length} record${filteredRows.length !== 1 ? "s" : ""}`}
                sx={{ bgcolor: "#E6F6FC", color: BRAND_DARK, fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`${generatedFor.from} → ${generatedFor.to}`}
                sx={{ bgcolor: "#eef7ee", color: "#2e7d32", fontWeight: 700 }}
              />
              {generatedFor.status && (
                <Chip
                  size="small"
                  label={`Status: ${generatedFor.status}`}
                  sx={{ bgcolor: "#fff4e5", color: "#b26a00", fontWeight: 700 }}
                />
              )}
              <TextField
                fullWidth={false}
                size="small"
                placeholder="Filter by ID or name..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{
                  width: 240,
                  "& input": { fontSize: 14 },
                  "& input::placeholder": { fontSize: 12 },
                }}
                InputProps={{
                  sx: { height: 36, paddingRight: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
        </ThemeProvider>

        {/* ── Table ── */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={32} sx={{ color: BRAND }} />
          </Box>
        ) : generatedFor ? (
          <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {activeColumns.map((c) => (
                      <TableCell key={c.field}>{c.header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeColumns.length} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="#999">
                          {search
                            ? "No rows match your filter"
                            : "No records for this report and range"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row, idx) => (
                      <TableRow key={`${row.emp_id}-${idx}`} hover>
                        {activeColumns.map((c) => {
                          const v = row[c.field];
                          const display =
                            v === null || v === undefined || v === "" ? "—" : String(v);
                          const isDatesCol =
                            c.field.includes("dates") || c.field === "leave_description";
                          return (
                            <TableCell
                              key={c.field}
                              sx={
                                isDatesCol
                                  ? {
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      maxWidth: 320,
                                    }
                                  : undefined
                              }
                            >
                              {display}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              py: 8,
              textAlign: "center",
              border: "1px dashed #d3d3d3",
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="body2" color="#999">
              Select a report and date range, then click <strong>Generate</strong> to preview.
            </Typography>
          </Box>
        )}

        {/* ── Snackbar ── */}
        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((p) => ({ ...p, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </MainLayout>
    </ThemeProvider>
  );
}
